/* eslint-disable no-bitwise */
/* eslint-disable no-restricted-syntax */

import XLSX from 'xlsx';
import { convertGramsToLargeUnits, weightLargeUnitLabel } from '../unitConversion';

class XLSXBinSetLevelPredictionsStrategy {
  constructor(metric, timezone) {
    this.isMetric = metric;
    this.timezone = timezone;
    this.sheetRows = [];
    this.binSetNames = [];
    this.allTimestamps = new Set();
    this.binSetData = new Map(); // Map of binSetId -> Map of timestamp -> mass
  }

  setBinSetObjects = (binSets) => {
    // Extract bin set names and initialize data structures
    binSets.forEach((binSet) => {
      this.binSetNames.push(`Bins ${binSet.bins.map((bin) => bin.name).join(',')}`);
    });
  };

  processBinSetLevelHistory = (binSetId, binSetHistoryData) => {
    if (!binSetHistoryData?.bin_set_history || binSetHistoryData.bin_set_history.length === 0) {
      return;
    }

    // Process the bin_set_history data for this bin set
    const binSetDataMap = new Map();

    binSetHistoryData.bin_set_history.forEach((entry) => {
      if (entry) {
        try {
          // Parse the bin_set_history JSON data
          if (entry.timestamp && entry.inventory_grams !== undefined) {
            const timestamp = entry.timestamp;
            const mass = entry.inventory_grams;

            this.allTimestamps.add(timestamp);
            binSetDataMap.set(timestamp, mass);
          }
        } catch (error) {
          console.error('Error parsing bin_set_history for bin set', binSetId, error);
        }
      }
    });

    this.binSetData.set(binSetId, binSetDataMap);
  };

  // Helper method to create sheet data with proper formatting
  createSheetData = (sheetRows) => {
    const sheetData = {};
    const range = { s: { r: 0, c: 0 }, e: { r: sheetRows.length - 1, c: sheetRows[0].length - 1 } };
    sheetData['!ref'] = XLSX.utils.encode_range(range);

    // Add header row
    sheetRows[0].forEach((cell, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      sheetData[cellAddress] = { v: cell };
    });

    // Add data rows with proper date and number formatting
    const dateFormat = 'yyyy-mm-dd HH:mm';
    const numberFormat = '#,##0.00'; // Format for mass values

    for (let row = 1; row < sheetRows.length; row++) {
      sheetRows[row].forEach((cell, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });

        if (colIndex === 0 && cell instanceof Date) {
          // Format date column
          sheetData[cellAddress] = {
            v: cell,
            t: 'd',
            z: dateFormat,
          };
        } else if (colIndex > 0 && typeof cell === 'number') {
          // Format numeric mass columns
          sheetData[cellAddress] = {
            v: cell,
            t: 'n',
            z: numberFormat,
          };
        } else if (cell === null || cell === undefined) {
          // Empty cells
          sheetData[cellAddress] = { t: 'e', v: 0x2a };
        } else {
          // Regular cells
          sheetData[cellAddress] = { v: cell };
        }
      });
    }

    // Set column widths
    const columnWidths = [
      { wch: 20 }, // Date column width
      ...Array(sheetRows[0].length - 1).fill({ wch: 15 }), // Data columns width
    ];
    sheetData['!cols'] = columnWidths;

    return sheetData;
  };

  createFile = () => {
    // Convert timestamps to sorted array
    const sortedTimestamps = Array.from(this.allTimestamps).sort((a, b) => a - b);

    // Filter timestamps to only include hour marks
    const filteredTimestamps = sortedTimestamps.filter((timestamp) => timestamp % (60 * 60) === 0);

    const workbook = XLSX.utils.book_new();

    // Create "All" sheet with all bin sets
    const allSheetRows = [];

    // Create header row for "All" sheet
    const allHeaderRow = [`Date (${this.timezone})`];
    this.binSetNames.forEach((name) => {
      allHeaderRow.push(`${name} (${weightLargeUnitLabel(this.isMetric)})`);
    });
    allSheetRows.push(allHeaderRow);

    // Create data rows for "All" sheet
    filteredTimestamps.forEach((timestamp) => {
      const dateRow = [new Date(timestamp * 1000)];

      this.binSetNames.forEach((_, index) => {
        const binSetId = Array.from(this.binSetData.keys())[index];
        const binSetDataMap = this.binSetData.get(binSetId);
        const mass = binSetDataMap?.get(timestamp);

        if (mass !== undefined) {
          // Store the converted numeric value, not the string
          const convertedMass = convertGramsToLargeUnits(this.isMetric, mass, null);
          dateRow.push(typeof convertedMass === 'number' ? convertedMass : parseFloat(convertedMass));
        } else {
          dateRow.push(null); // Empty cell if no data for this timestamp
        }
      });
      allSheetRows.push(dateRow);
    });

    // Add "All" sheet to workbook
    const allSheetData = this.createSheetData(allSheetRows);
    XLSX.utils.book_append_sheet(workbook, allSheetData, 'All');

    // Create individual sheets for each bin set
    this.binSetNames.forEach((binSetName, index) => {
      const binSetId = Array.from(this.binSetData.keys())[index];
      const binSetDataMap = this.binSetData.get(binSetId);

      const individualSheetRows = [];

      // Create header row for individual sheet
      const individualHeaderRow = [`Date (${this.timezone})`, `${binSetName} (${weightLargeUnitLabel(this.isMetric)})`];
      individualSheetRows.push(individualHeaderRow);

      // Create data rows for individual sheet
      filteredTimestamps.forEach((timestamp) => {
        const dateRow = [new Date(timestamp * 1000)];
        const mass = binSetDataMap?.get(timestamp);

        if (mass !== undefined) {
          // Store the converted numeric value, not the string
          const convertedMass = convertGramsToLargeUnits(this.isMetric, mass, null);
          dateRow.push(typeof convertedMass === 'number' ? convertedMass : parseFloat(convertedMass));
        } else {
          dateRow.push(null); // Empty cell if no data for this timestamp
        }

        individualSheetRows.push(dateRow);
      });

      // Add individual sheet to workbook
      const individualSheetData = this.createSheetData(individualSheetRows);

      // Clean up sheet name to ensure it's valid for Excel
      const cleanSheetName = binSetName.replace(/[\\/?*[\]]/g, '').substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, individualSheetData, cleanSheetName);
    });

    const s2ab = (s) => {
      if (typeof ArrayBuffer !== 'undefined') {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; i += 1) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
      }
      const buf = new Array(s.length);
      for (let i = 0; i !== s.length; i += 1) buf[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };

    const wopts = { bookType: 'xlsx', bookSST: false, type: 'binary' };
    const wbout = XLSX.write(workbook, wopts);
    return new Blob([s2ab(wbout)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };
}

export default XLSXBinSetLevelPredictionsStrategy;
